class BmCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prevBtn = this.querySelector('.bm-carousel__nav--prev');
    this.nextBtn = this.querySelector('.bm-carousel__nav--next');
    if (!this.track) return;

    this.autoplayMs = parseInt(this.dataset.autoplayMs, 10) || 3500;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isAnimating = false;
    this.loopReady = false;

    this.setupInfiniteLoop();

    this.prevBtn?.addEventListener('click', () => { this.step(-1); this.restartAutoplay(); });
    this.nextBtn?.addEventListener('click', () => { this.step(1); this.restartAutoplay(); });

    ['mouseenter', 'focusin', 'touchstart'].forEach((evt) => {
      this.addEventListener(evt, () => this.stopAutoplay(), { passive: true });
    });
    ['mouseleave', 'focusout'].forEach((evt) => {
      this.addEventListener(evt, () => this.restartAutoplay());
    });

    this.track.addEventListener('scrollend', () => this.onScrollSettled());
    window.addEventListener('resize', () => this.recalcLoopBounds());

    this.updateNavState();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    clearTimeout(this._settleFallback);
  }

  setupInfiniteLoop() {
    const items = Array.from(this.track.children);
    this.isLooping = items.length >= 2;
    if (!this.isLooping) {
      this.loopReady = true;
      if (!this.reducedMotion) this.startAutoplay();
      return;
    }

    this.leadingClones = items.map((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input').forEach((el) => { el.tabIndex = -1; });
      return clone;
    });
    this.trailingClones = items.map((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input').forEach((el) => { el.tabIndex = -1; });
      return clone;
    });

    const fragmentBefore = document.createDocumentFragment();
    this.leadingClones.forEach((clone) => fragmentBefore.appendChild(clone));
    this.track.insertBefore(fragmentBefore, this.track.firstChild);

    const fragmentAfter = document.createDocumentFragment();
    this.trailingClones.forEach((clone) => fragmentAfter.appendChild(clone));
    this.track.appendChild(fragmentAfter);

    this.realFirstItem = items[0];

    // Setting scrollLeft right after inserting scroll-snap children gets silently
    // overridden back to 0 once the browser recalculates snap targets. Disabling
    // snap for the jump (with a forced reflow in between) sidesteps that instead
    // of racing it with rAF, which can be throttled/paused in background tabs.
    const prevSnapType = this.track.style.scrollSnapType;
    this.track.style.scrollSnapType = 'none';
    this.recalcLoopBounds();
    this.track.scrollLeft = this.realStart;
    void this.track.offsetHeight;
    this.track.style.scrollSnapType = prevSnapType;

    this.loopReady = true;
    if (!this.reducedMotion) this.startAutoplay();
  }

  recalcLoopBounds() {
    if (!this.isLooping) return;
    this.realStart = this.realFirstItem.offsetLeft;
    this.realEnd = this.trailingClones[0].offsetLeft;
    this.contentWidth = this.realEnd - this.realStart;
  }

  itemWidth() {
    const item = this.track.querySelector('.bm-carousel__item');
    if (!item) return 300;
    const style = getComputedStyle(this.track);
    return item.getBoundingClientRect().width + parseFloat(style.gap || 20);
  }

  step(direction) {
    if (this.isAnimating || !this.loopReady) return;
    this.isAnimating = true;
    const amount = this.itemWidth() * direction;
    this.track.scrollBy({ left: amount, behavior: this.reducedMotion ? 'auto' : 'smooth' });

    clearTimeout(this._settleFallback);
    this._settleFallback = setTimeout(() => this.onScrollSettled(), 700);
  }

  onScrollSettled() {
    this.correctLoopBoundary();
    this.updateNavState();
    this.isAnimating = false;
    clearTimeout(this._settleFallback);
  }

  correctLoopBoundary() {
    if (!this.isLooping || !this.contentWidth) return;
    const sl = this.track.scrollLeft;
    if (sl >= this.realEnd - 1) {
      this.track.scrollLeft = sl - this.contentWidth;
    } else if (sl < this.realStart - 1) {
      this.track.scrollLeft = sl + this.contentWidth;
    }
  }

  updateNavState() {
    if (!this.prevBtn || !this.nextBtn) return;
    const scrollable = this.isLooping || this.track.scrollWidth > this.track.clientWidth + 4;
    this.prevBtn.disabled = !scrollable;
    this.nextBtn.disabled = !scrollable;
    this.prevBtn.style.visibility = scrollable ? 'visible' : 'hidden';
    this.nextBtn.style.visibility = scrollable ? 'visible' : 'hidden';
  }

  startAutoplay() {
    this.stopAutoplay();
    this._interval = setInterval(() => this.step(1), this.autoplayMs);
  }

  stopAutoplay() {
    clearInterval(this._interval);
  }

  restartAutoplay() {
    if (!this.reducedMotion) this.startAutoplay();
  }
}

customElements.define('bm-carousel', BmCarousel);
